import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, MonitorPlay } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Crest } from "@/components/Brand";
import { api, errMsg } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";

const KIND = { departure: "Departs", arrival: "Arrives", stop: "Stops" };

function statusOf(r) {
  if (r.kind === "departure") {
    if (r.status === "departed") return ["DEPARTED", "text-slate-400"];
    if (r.status === "boarding") return ["BOARDING", "text-[#FCD116]"];
    return ["ON TIME", "text-emerald-300"];
  }
  if (r.status === "arrived") return [`ARRIVED ${fmtTime(r.actual)}`, "text-emerald-300"];
  if (r.status === "passed") return ["PASSED", "text-slate-400"];
  return r.delay_min > 0 ? [`EXPECTED ${fmtTime(r.eta)}`, "text-rose-300"] : ["ON TIME", "text-emerald-300"];
}

function BoardRow({ r }) {
  const [txt, cls] = statusOf(r);
  return (
    <div className="grid grid-cols-[64px_1fr_auto] items-center gap-3 border-b border-white/10 px-4 py-3 font-mono text-sm sm:grid-cols-[80px_90px_1fr_130px_170px]" data-testid={`board-row-${r.trip_id}`}>
      <span className="text-lg font-bold text-[#FCD116]">{fmtTime(r.scheduled)}</span>
      <span className="hidden text-xs uppercase text-blue-200 sm:block">{KIND[r.kind]}</span>
      <span className="min-w-0"><b className="block truncate text-white">{r.kind === "arrival" ? `from ${r.from_city}` : `to ${r.to_city}`}</b><span className="block truncate text-[11px] text-blue-200">{r.next_stops.length ? `via ${r.next_stops.join(" · ")}` : r.route}</span></span>
      <span className="hidden text-blue-100 sm:block">{r.bus_number}</span>
      <span className={`text-right font-bold ${cls}`} data-testid={`board-status-${r.trip_id}`}>{txt}</span>
    </div>
  );
}

export default function ArrivalsBoard() {
  const { stationId } = useParams();
  const nav = useNavigate();
  const [stations, setStations] = useState([]);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/stations").then((r) => setStations(r.data)); }, []);
  useEffect(() => { if (!stationId && stations.length) nav(`/arrivals/${stations.find((s) => s.code === "ACC")?.id || stations[0].id}`, { replace: true }); }, [stationId, stations, nav]);
  const load = useCallback(() => stationId && api.get(`/public/stations/${stationId}/board`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [stationId]);
  useEffect(() => { setD(null); load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="arrivals-board-page">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-3">
          <Crest className="h-12 w-12" />
          <div><h1 className="font-display text-3xl font-extrabold text-[#182678]">Live station board</h1><p className="text-sm text-slate-500">Departures and arrivals, updated by station masters and bus GPS.</p></div>
        </div>
        <Select value={stationId || ""} onValueChange={(v) => nav(`/arrivals/${v}`)}>
          <SelectTrigger className="h-11 w-full rounded-xl sm:w-64" data-testid="board-station-select"><SelectValue placeholder="Choose station" /></SelectTrigger>
          <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} · {s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-hidden rounded-3xl bg-[#0E1A5C] shadow-[0_30px_60px_-30px_rgba(14,26,92,0.8)]" data-testid="station-board">
        <div className="flex items-center justify-between bg-[#182678] px-4 py-3">
          <span className="flex items-center gap-2 font-display text-lg font-extrabold text-white"><MonitorPlay className="h-5 w-5 text-[#FCD116]" />{d?.station.city || "..."} station</span>
          <span className="font-mono text-xs text-blue-200" data-testid="board-updated">{d ? `Updated ${fmtTime(d.generated_at)} GMT` : ""}</span>
        </div>
        <div className="hidden grid-cols-[80px_90px_1fr_130px_170px] gap-3 border-b border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-blue-300 sm:grid"><span>Time</span><span>Type</span><span>Destination / origin</span><span>Bus</span><span className="text-right">Status</span></div>
        {!d ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>
          : d.rows.length ? d.rows.map((r) => <BoardRow key={r.trip_id} r={r} />)
            : <div className="p-10 text-center font-mono text-sm text-blue-200" data-testid="board-empty">No buses due at this station in the next 24 hours.</div>}
      </div>
    </div>
  );
}
